import { observer } from 'mobx-react';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import SvgIcon from '@/assets/svgIcon';
import ImageViewer from '@/components/ImageViewer';
import { Spinner } from '@/components/Spinner';
import { useFetchUserDetail } from '@/services';
import {
  useEditMessage,
  useFetchMessages,
  useFetchNewMessages,
  useReadMessage,
} from '@/services/chatService';
import { chatKey } from '@/services/queryKey';

import { GroupStatus } from '../constants';
import { FileDto } from '../dto';
import { useObserver } from '../hooks';
import { useChatStore } from '../store';
import { Message } from './Message';

export const MESSAGE_FETCH_SIZE = 20;

export const ChatContent = observer(() => {
  const contentRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef<any>(0);
  const store = useChatStore();
  const top = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { venueId } = useParams();
  const [targetImageIdx, setTargetImageIdx] = useState(0);
  const [images, setImages] = useState<FileDto[]>([]);
  const queryClient = useQueryClient();
  const myInfo = useFetchUserDetail();
  useEffect(() => {
    setIsFirstLoad(true);
    prevScrollHeight.current = 0;
    store.emptyMessageMap();
    queryClient.invalidateQueries(
      chatKey.fetchMessages(store.currentGroup?.id)
    );
    // queryClient.invalidateQueries(chatKey.fetchGroups(computedVenueId));
  }, [store.currentGroup?.id]);

  useEffect(() => {
    queryClient.invalidateQueries(
      chatKey.fetchNewMessages(store.currentGroup.id)
    );
  }, [store.currentGroup.unread]);

  const computedVenueId = venueId
    ? Number(venueId)
    : store.currentGroup.venueId;
  const scrollToBottom = () => {
    contentRef.current?.scrollTo(0, contentRef.current?.scrollHeight);
  };

  const { fetchNextPage, hasNextPage, isFetching } = useFetchMessages(
    {
      venueId: computedVenueId,
      groupId: store.currentGroup?.id,
      size: MESSAGE_FETCH_SIZE,
    },
    {
      onSuccess: function () {
        if (isFirstLoad) {
          scrollToBottom();
          setIsFirstLoad(false);
        }
      },
    }
  );

  useFetchNewMessages({
    venueId: computedVenueId,
    groupId: store.currentGroup?.id,
    size: MESSAGE_FETCH_SIZE,
  });

  const { mutate: readMessage } = useReadMessage(
    computedVenueId,
    function onSuccess() {},
    function onError() {
      //TODO: 에러 처리
    }
  );

  const scrollToLastRead = () => {
    contentRef.current?.scrollTo(
      0,
      contentRef.current?.scrollHeight - prevScrollHeight.current
    );
  };

  const handleTopIntersect: IntersectionObserverCallback = ([entry]) => {
    if (store.messageList.length === 0) return;
    if (!entry.isIntersecting || isFetching) return;
    if (hasNextPage) {
      prevScrollHeight.current = contentRef.current?.scrollHeight;
      fetchNextPage();
    }
  };

  const handleBottomIntersect: IntersectionObserverCallback = ([entry]) => {
    entry.isIntersecting && store.currentGroup.unread && handleRead();
  };

  const handleRead = async () => {
    if (store.messageList.length === 0) return;
    if (store.currentGroup.id === -1) return;
    if (computedVenueId === -1) return;
    if (store.currentGroup.unread) {
      await readMessage({
        groupId: store.currentGroup?.id,
        lastMessageId: store.lastMessageId,
      });
      store.currentGroup.unread = false;
    }
  };

  useObserver({
    target: top,
    onIntersect: handleTopIntersect,
  });

  useObserver({
    target: bottom,
    onIntersect: handleBottomIntersect,
  });

  const { mutate: editMessage } = useEditMessage(
    function onSuccess(res) {
      store.setMessageMapFromData([res.data]);
    },
    function onError(error) {
      // TODO: 에러처리
    }
  );

  /**
   * 상황 별 스크롤 위치를 계산하기 위한 effect
   */
  useEffect(() => {
    if (store.myMessageChecker) {
      // 내가 발생시킨 새로운 메시지 case
      store.myMessageChecker = false;
      scrollToBottom();
    } else {
      // 스크롤을 올려 메시지를 추가 load했을 경우
      scrollToLastRead();
    }
  }, [store.messageList.length]);

  return (
    <div
      ref={contentRef}
      className="box-border flex flex-1 flex-col overflow-y-auto max-md:mt-[74px]"
    >
      {!venueId && store.groupList.length === 0 ? (
        <div className="flex flex-col items-center gap-2 justify-center h-full">
          <img
            width={240}
            height={240}
            src={'/image/empty.png'}
            alt={'없습니다.'}
          />
          <p className="font-medium text-xl text-gray-900">
            문의/답변 기록이 없습니다.
          </p>
          <p className="font-normal text-gray-500 text-base">
            원하는 지점을 선택하여 메시지를 전송해보세요.
          </p>
        </div>
      ) : (
        <Suspense fallback={<Spinner />}>
          <div ref={top} />
          {store.messageList.map((message) => (
            <Message
              disabled={store.currentGroup?.status === GroupStatus.CLOSED}
              setImages={setImages}
              setTargetImageIdx={setTargetImageIdx}
              message={message}
              editMessage={editMessage}
              myInfo={myInfo}
              key={message.id}
            />
          ))}
          {store.currentGroup.unread && (
            <button
              onClick={scrollToBottom}
              className="sticky bottom-3 flex w-[161px] items-center justify-center gap-2 self-center rounded-full bg-[#0e2130] px-4 py-3 opacity-90 shadow-md"
            >
              <p className="text-base font-semibold text-white">
                새 메시지 도착
              </p>
              <SvgIcon width={16} height={16} name="subtractIcon" />
            </button>
          )}
          <div ref={bottom} />
          {images.length > 0 && (
            <ImageViewer
              isOpen={images.length > 0}
              imageName={images[targetImageIdx]?.name}
              imageUrl={images[targetImageIdx]?.path}
              imageThumbnailUrl={images[targetImageIdx]?.thumbnailPath}
              prevImageUrl={
                images[(targetImageIdx + images.length - 1) % images.length]
                  ?.path
              }
              prevImageThumbnailUrl={
                images[(targetImageIdx + images.length - 1) % images.length]
                  ?.thumbnailPath
              }
              nextImageUrl={images[(targetImageIdx + 1) % images.length]?.path}
              nextImageThumbnailUrl={
                images[(targetImageIdx + 1) % images.length].thumbnailPath
              }
              onPrevImageRequest={() => {
                setTargetImageIdx(
                  (targetImageIdx + images.length - 1) % images.length
                );
              }}
              onNextImageRequest={() => {
                setTargetImageIdx((targetImageIdx + 1) % images.length);
              }}
              onClose={() => {
                setImages([]);
              }}
            />
          )}
        </Suspense>
      )}
    </div>
  );
});
